import { useMutation, useQuery } from '@apollo/client';
import React from 'react';
import useNavigate from '../../utils/router/useNavigate';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import Form, { Field, Submit } from '../../components/Form';
import LoadingIndicator from '../../components/LoadingIndicator';
import Modal, { ModalTitle } from '../../components/Modal';
import Tooltip from '../../components/Tooltip';
import {
  BillingSummaryStatus,
  PlanTier,
} from '../../graphql/generated/graphql';
import SettingCard from '../../settings/SettingCard';
import * as styles from './DeleteTeam.module.scss';
import { gql } from '../../graphql/generated/gql';
import { UIActions } from '../../ui/uiSlice';

const MODAL_WIDTH = 370;

const DELETE_ORGANIZATION_MUTATION = gql(/** @complexity 2 */ `
  mutation DeleteOrganization {
    deleteOrganization {
      id
    }
  }
`);

interface DeleteTeamModalProps {
  close: () => void;
}

type Values = {
  delete: string;
};

export const DeleteTeamModal = ({ close }: DeleteTeamModalProps) => {
  const navigate = useNavigate();

  const [deleteOrganization] = useMutation(DELETE_ORGANIZATION_MUTATION);

  const onSubmit = async (values: Values) => {
    if (values.delete === 'delete') {
      try {
        close();
        navigate('/deleted', { state: { isDeleted: true } });
        await deleteOrganization();
      } catch (err) {
        navigate('/admin/import');
      }
    }
  };

  return (
    <Modal width={MODAL_WIDTH} noPadding>
      <div className={styles.wrapper}>
        <ModalTitle small close={close} noMargin>
          Delete team
        </ModalTitle>
        <div className={styles.description}>
          Deleting a Slab team cannot be undone. All topics, posts, and content
          will no longer be retrievable.
          <br />
          <br />
          Type <strong>delete</strong> below to confirm.
        </div>
        <Form<Values> initialValues={{ delete: '' }} onSubmit={onSubmit}>
          {({ values }) => {
            // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
            const deleteEnabled = values.delete === 'delete';
            return (
              <>
                <Field type="text" name="delete" placeholder="delete" />
                <div className={styles.buttons}>
                  <Button variant="secondary" onClick={() => close()}>
                    Cancel
                  </Button>
                  <Submit
                    disabled={!deleteEnabled}
                    variant="secondary"
                    danger={deleteEnabled}
                  >
                    Delete team
                  </Submit>
                </div>
              </>
            );
          }}
        </Form>
      </div>
    </Modal>
  );
};

const DELETE_TEAM_QUERY = gql(/** @complexity 4 */ `
  query DeleteTeam {
    billingSummary {
      id
      status
    }
  }
`);

const DeleteTeam = () => {
  const dispatch = useAppDispatch();
  const planTier = useAppSelector(
    (state) => state.session.organization.planTier,
  );

  const { data, loading } = useQuery(DELETE_TEAM_QUERY);

  const canDelete =
    planTier === PlanTier.Free ||
    data?.billingSummary.status !== BillingSummaryStatus.Active;

  const openModal = () => {
    dispatch(UIActions.openModal(DeleteTeamModal));
  };

  return (
    <SettingCard
      title="Delete team"
      description="Permanently delete this Slab team"
      actions={
        loading ? (
          <LoadingIndicator />
        ) : (
          <Tooltip
            content={
              canDelete ? undefined : 'Please cancel your\nsubscription first'
            }
          >
            <Button
              onClick={openModal}
              variant="secondary"
              disabled={!canDelete}
              danger
            >
              Delete team
            </Button>
          </Tooltip>
        )
      }
    />
  );
};

export default DeleteTeam;
